/**
 * ModulePage — a dedicated PAGE for one module/service (the `module` tab kind),
 * opened by clicking an item in the index. It is a PAGE FIRST: a centered document
 * column with the module's rich inspector (name · type · description · fields ·
 * aspects · interfaces · connections · processes · submodules · blast radius), and
 * a board EMBEDDED as a bounded hero at the top — not a full board surface.
 *
 * The embedded board is a store-free {@link ArchitectureView} fed the synthesized
 * {@link computeModuleBoardPlan} projection (`show <this module>` — the engine
 * auto-derives the in/out stubs), so the embed shows the module's internals plus
 * every incoming/outgoing connection as a boundary port pill. It is bounded and
 * `wheel={false}` so it reads as an embedded figure inside the scrolling page, not
 * a board that took over the surface. The detail below is the same
 * {@link DetailsPanel} the board's right rail uses.
 *
 * Navigation: the page is pinned to THIS module; clicking a connection row, a
 * board node, or a boundary stub opens THAT module's own page. "Open board" drops
 * to the full board rooted at this module.
 */
import { useCallback, useMemo, type ReactNode } from "react";
import { Button } from "@archlang/ui";
import { buildBoardFromModules } from "@archlang/scene/board";
import { flattenModulesWithParent, type DependencyEdge, type Module } from "@archlang/scene/arch-model";
import { ArchitectureView } from "../architecture-view/ArchitectureView.js";
import { DetailsPanel } from "../architecture-view/DetailsPanel.js";
import { computeModuleBoardPlan } from "../views/moduleBoardPlan.js";
import { useWorkspaceModel } from "./WorkspaceModelContext.js";
import { useOptionalWorkspace } from "./WorkspaceContext.js";
import { useModelIndexes } from "../hooks/useModelIndexes.js";
import { displayLabel } from "../displayLabel.js";
import { useTranslation } from "../i18n";

export interface ModulePageProps {
  readonly moduleId: string;
  /** Tab-state label — used before the model resolves the module. */
  readonly moduleName?: string;
}

export function ModulePage({ moduleId, moduleName }: ModulePageProps): ReactNode {
  const { t } = useTranslation();
  const modelApi = useWorkspaceModel();
  const { model, edges, resolvedModel, graph, compiledSources } = modelApi;
  const store = useOptionalWorkspace();
  const indexes = useModelIndexes(model);
  const module = indexes?.moduleById.get(moduleId) ?? null;

  // The embedded board's projection: this module shown with its subtree, its
  // in/out connections auto-derived as boundary stubs. Null (no embed) until the
  // model resolves or if the key is unrepresentable — the detail still stands.
  const plan = useMemo(
    () => computeModuleBoardPlan(resolvedModel, graph, compiledSources, moduleId),
    [resolvedModel, graph, compiledSources, moduleId],
  );

  // Full-model board root so DetailsPanel indexes every entity and resolves this
  // module's connections across the whole model (positions unused).
  const root = useMemo(() => {
    if (!model) return null;
    return buildBoardFromModules(
      flattenModulesWithParent(model.modules as unknown as Module[]),
      edges as unknown as DependencyEdge[],
      new Map(),
      new Map(),
      false,
    );
  }, [model, edges]);

  const openModule = useCallback(
    (id: string | null) => {
      if (id && id !== moduleId) store?.openTab({ kind: "module", moduleId: id });
    },
    [store, moduleId],
  );

  const openBoard = useCallback(() => {
    store?.openTab({
      kind: "board",
      rootId: moduleId,
      rootIsSpace: (module?.children?.length ?? 0) > 0,
    });
  }, [store, moduleId, module]);

  if (!model || !root) return null;
  const name = displayLabel(module?.name ?? moduleName ?? moduleId);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col" data-testid="module-page">
      <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-2">
        <span className="truncate text-sm font-semibold text-slate-100" title={name}>
          {name}
        </span>
        <Button
          variant="ghost"
          size="xs"
          onClick={openBoard}
          data-testid="module-page-open-board"
          className="ml-auto"
        >
          {t("moduleOpenBoard")}
        </Button>
      </div>
      {/* ONE scroll container for the whole page: the embedded board figure and
          the detail scroll together as a single document. Centered + width-capped
          so it reads as a page. The detail renders at its natural height (block
          context) so the outer column — not the inspector — owns the scroll. */}
      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="module-page-scroll">
        <div className="mx-auto w-full max-w-4xl px-4 py-4">
          {plan ? (
            <div
              className="relative mb-4 h-80 overflow-hidden rounded-lg border border-slate-800 bg-slate-900/40"
              data-testid="module-page-board"
            >
              <ArchitectureView
                modelState={modelApi}
                viewPlan={plan}
                selectedId={moduleId}
                onSelectChange={openModule}
                onItemOpenRooted={openModule}
                onRevealBoundary={openModule}
                wheel={false}
              />
            </div>
          ) : null}
          <div data-testid="module-page-detail">
            <DetailsPanel
              model={model}
              root={root}
              selectedId={moduleId}
              selectedEdgeId={null}
              onSelectModule={openModule}
              onSelectEdge={() => {}}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
